import React, { useState, useEffect } from 'react';
import { HighlightClip, SportType } from '../types';
import { X, Link2, Sparkles, Film } from 'lucide-react';
import { getHostedVideoEmbedUrl } from '../utils/videoProvider';

interface UploadHighlightModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddHighlight: (clip: HighlightClip) => void;
  presetTitle?: string;
  presetSport?: SportType;
}

export const UploadHighlightModal: React.FC<UploadHighlightModalProps> = ({
  isOpen,
  onClose,
  onAddHighlight,
  presetTitle,
  presetSport,
}) => {
  const [activeTab, setActiveTab] = useState<'link' | 'sample'>('link');
  const [title, setTitle] = useState('');
  const [sport, setSport] = useState<SportType>('basketball');
  const [description, setDescription] = useState('');

  // Prefill title & sport if preset provided
  useEffect(() => {
    if (isOpen) {
      if (presetTitle) setTitle(presetTitle);
      if (presetSport) setSport(presetSport);
    }
  }, [isOpen, presetTitle, presetSport]);
  
  const [hostedVideoUrl, setHostedVideoUrl] = useState('');
  const [formError, setFormError] = useState('');

  // Sample highlights
  const sampleClips = [
    {
      title: 'Clutch Game-Winner Crossover 🏀',
      sport: 'basketball' as SportType,
      videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-basketball-player-dunking-a-ball-40866-large.mp4',
      thumbnailUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&q=80&w=600',
      durationSeconds: 18,
    },
    {
      title: 'Walk-off 9th Inning Home Run Blast ⚾',
      sport: 'baseball' as SportType,
      videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-baseball-player-batting-in-a-stadium-41584-large.mp4',
      thumbnailUrl: 'https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&q=80&w=600',
      durationSeconds: 24,
    },
    {
      title: '40-Yard Touchdown Pass in the Clutch 🏈',
      sport: 'football' as SportType,
      videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-american-football-players-during-a-game-41857-large.mp4',
      thumbnailUrl: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?auto=format&fit=crop&q=80&w=600',
      durationSeconds: 29,
    },
    {
      title: 'Unstoppable Pickleball Kitchen Dink Rally 🏓',
      sport: 'pickleball' as SportType,
      videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-tennis-player-hitting-a-ball-41582-large.mp4',
      thumbnailUrl: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&q=80&w=600',
      durationSeconds: 15,
    },
  ];

  const [selectedSample, setSelectedSample] = useState(sampleClips[0]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!title.trim()) {
      setFormError('Please add a title for your highlight.');
      return;
    }

    const videoUrl = activeTab === 'sample' ? selectedSample.videoUrl : hostedVideoUrl.trim();
    if (!videoUrl || (activeTab === 'link' && !getHostedVideoEmbedUrl(videoUrl))) {
      setFormError('Use a valid YouTube watch/shorts link or Vimeo video link.');
      return;
    }
    let thumbnailUrl = selectedSample.thumbnailUrl;

    const newClip: HighlightClip = {
      id: `clip_${Date.now()}`,
      title: title.trim(),
      sport,
      videoUrl,
      thumbnailUrl,
      durationSeconds: selectedSample.durationSeconds,
      createdAt: 'Just now',
      views: 1,
      likes: 0,
      description: description.trim() || 'Game highlight clip uploaded on Playgrounds.',
    };

    onAddHighlight(newClip);
    onClose();

    // Reset
    setTitle('');
    setDescription('');
    setHostedVideoUrl('');
    setFormError('');
  };

  return (
    <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-indigo-900/90 text-white rounded-[2.5rem] border border-white/15 shadow-2xl w-full max-w-lg overflow-hidden p-6 my-8 relative space-y-5">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-indigo-200 hover:text-white rounded-full bg-indigo-950/80 border border-white/10 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="text-center space-y-1">
          <div className="w-12 h-12 rounded-2xl bg-lime-400 text-black mx-auto flex items-center justify-center font-black text-xl shadow-lg shadow-lime-400/20">
            <Film className="w-6 h-6 stroke-[2.5]" />
          </div>
          <h2 className="text-2xl font-black italic uppercase tracking-tight text-white pt-2">
            Add Hosted Highlight Clip
          </h2>
          <p className="text-xs text-indigo-200/70 font-semibold">
            Share clutch plays, dunks, home runs, or rally clips directly on your athlete reel.
          </p>
        </div>

        {/* Hosted provider selector */}
        <div className="flex bg-indigo-950 p-1 rounded-2xl border border-white/10">
          <button
            type="button"
            onClick={() => setActiveTab('link')}
            className={`flex-1 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center justify-center space-x-1.5 ${
              activeTab === 'link' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
            }`}
          >
            <Link2 className="w-4 h-4" />
            <span>YouTube / Vimeo</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('sample')}
            className={`flex-1 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center justify-center space-x-1.5 ${
              activeTab === 'sample' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            <span>Presets</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-5 bg-indigo-950/60 rounded-2xl border border-white/10 space-y-3">
            <label className="text-xs font-bold text-indigo-200 block">Hosted video URL *</label>
            <input
              type="url"
              required
              value={hostedVideoUrl}
              onChange={(e) => setHostedVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
            />
            <p className="text-[10px] text-indigo-200/70 font-semibold">Upload the clip to YouTube or Vimeo first. Playground stores only the hosted link, never the video file.</p>
            {getHostedVideoEmbedUrl(hostedVideoUrl) && (
              <div className="aspect-video overflow-hidden rounded-xl border border-white/10 bg-black">
                <iframe
                  src={getHostedVideoEmbedUrl(hostedVideoUrl)!}
                  title="Highlight preview"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            )}
          </div>

          {/* Tab Content: Preset Samples */}
          {activeTab === 'sample' && (
            <div className="space-y-2">
              <label className="text-xs font-bold text-indigo-200 block">Select Sample Highlight</label>
              <div className="grid grid-cols-2 gap-2">
                {sampleClips.map((clip, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setSelectedSample(clip);
                      setTitle(clip.title);
                      setSport(clip.sport);
                    }}
                    className={`p-2 rounded-2xl border text-left flex items-center space-x-2.5 transition ${
                      selectedSample.title === clip.title
                        ? 'bg-lime-400/20 border-lime-400 text-white'
                        : 'bg-indigo-950 border-white/10 text-indigo-200 hover:text-white'
                    }`}
                  >
                    <img src={clip.thumbnailUrl} alt={clip.title} className="w-10 h-10 rounded-xl object-cover shrink-0" referrerPolicy="no-referrer" />
                    <div className="min-w-0">
                      <span className="text-xs font-extrabold block line-clamp-1">{clip.title}</span>
                      <span className="text-[10px] text-lime-400 font-mono font-bold">{clip.durationSeconds}s</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Form Fields: Title, Sport, Description */}
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">Clip Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. Ankle Breaker Stepback 3-Pointer!"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-indigo-200 block mb-1">Sport</label>
                <select
                  value={sport}
                  onChange={(e) => setSport(e.target.value as SportType)}
                  className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                >
                  <option value="basketball">Basketball 🏀</option>
                  <option value="baseball">Baseball ⚾</option>
                  <option value="softball">Softball 🥎</option>
                  <option value="pickleball">Pickleball 🏓</option>
                  <option value="soccer">Soccer ⚽</option>
                  <option value="volleyball">Volleyball 🏐</option>
                  <option value="football">Football 🏈</option>
                  <option value="tennis">Tennis 🎾</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-indigo-200 block mb-1">Clip Caption</label>
                <input
                  type="text"
                  placeholder="e.g. 4th Quarter game runner"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>
            </div>
          </div>

          {/* Submit Action */}
          {formError && (
            <p role="alert" className="text-xs font-bold text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-xl px-3 py-2">
              {formError}
            </p>
          )}
          <button
            type="submit"
            className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-sm rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition mt-2"
          >
            <Sparkles className="w-5 h-5 stroke-[2.5]" />
            <span>Publish Highlight to Profile (+50 XP)</span>
          </button>
        </form>
      </div>
    </div>
  );
};
